import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useAuthContext } from '@/context/AuthContext';

interface TopBarProps {
  onMenuClick: () => void;
  pageTitle: string;
}

export default function TopBar({ onMenuClick, pageTitle }: TopBarProps) {
  const { isDark, toggleTheme } = useApp();
  const { logout } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/reports?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`h-16 flex items-center gap-4 px-4 md:px-6 border-b sticky top-0 z-20
        ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
    >
      {/* Hamburger */}
      <button
        className={`w-9 h-9 flex items-center justify-center rounded-lg lg:hidden cursor-pointer
          ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
        onClick={onMenuClick}
      >
        <i className="ri-menu-line text-xl" />
      </button>

      {/* Page title */}
      <h2 className={`text-base font-semibold hidden md:block ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {pageTitle}
      </h2>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex-1 max-w-md ml-auto md:ml-4">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
          ${isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            placeholder="Search clients, transfers, items..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={`flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400
              ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
          />
        </div>
      </form>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors
          ${isDark ? 'text-yellow-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <span className="w-5 h-5 flex items-center justify-center">
          <i className={isDark ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
        </span>
      </button>

      {/* Avatar + Logout */}
      <div className="relative">
        <button
          onClick={() => setShowLogoutConfirm(v => !v)}
          className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold cursor-pointer
            ${isDark ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white'}`}
          title="Account"
        >
          B
        </button>

        {showLogoutConfirm && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setShowLogoutConfirm(false)}
            />
            <div className={`absolute right-0 top-11 z-40 w-44 rounded-xl border p-1
              ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className={`px-3 py-2 text-xs font-medium border-b mb-1
                ${isDark ? 'text-gray-400 border-gray-700' : 'text-gray-400 border-gray-100'}`}>
                Signed in as Admin
              </div>
              <button
                onClick={() => { setShowLogoutConfirm(false); logout(); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors whitespace-nowrap
                  ${isDark ? 'text-red-400 hover:bg-gray-800' : 'text-red-500 hover:bg-red-50'}`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-line" />
                </span>
                Lock Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
